import React, { forwardRef, useState } from 'react';
import { BaseButton } from 'lib/button/blocks/BaseButton';
import { getRem } from 'lib/core';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { getStateStyles } from './fabStateStyles';
import { BUTTON_BACKGROUND_APPEARANCES } from '../../constants';

const StyledFab = styled(BaseButton)`
  border-radius: 1.5rem;
  position: relative;
  height: ${getRem(48)};
  justify-content: end;
  min-width: ${getRem(48)};
  > svg {
    margin-right: ${({ isExpanded, text }) => (isExpanded === false || text.length === 0 ? '0' : getRem(4))};
    margin-left: ${({ isExpanded }) => (isExpanded ? getRem(4) : '')};
  }
  ::after {
    border-radius: 1.5rem;
    box-shadow: ${({ theme }) => theme.elevation[4].value};
  }
`;

// This styled component is only used for consuming teams to position their FAB correctly.
export const FabContainer = styled.div`
  position: fixed;
  bottom: 32px;
  right: 32px;
  z-index: ${({ theme }) => theme.zIndex.fab.value};
`;

const Fab = forwardRef(
  (
    {
      backgroundAppearance = BUTTON_BACKGROUND_APPEARANCES.LIGHT,
      dataTestId = undefined,
      icon = undefined,
      isCollapsible = false,
      isDisabled = false,
      onClick = () => {},
      text,
      ...other
    },
    ref
  ) => {
    const [isFocused, setIsFocused] = useState(false);
    const [isExpanded, setExpanded] = useState(!isCollapsible);

    const handleFocus = () => {
      if (isCollapsible) {
        setIsFocused(!isFocused);
        setExpanded(true);
      }
    };
    const handleBlur = () => {
      if (isCollapsible) {
        setIsFocused(!isFocused);
        setExpanded(false);
      }
    };

    return (
      <StyledFab
        onBlur={handleBlur}
        onFocus={handleFocus}
        onMouseEnter={handleFocus}
        onMouseLeave={handleBlur}
        stateStyles={getStateStyles(backgroundAppearance === BUTTON_BACKGROUND_APPEARANCES.PRIMARY)}
        text={isExpanded && text}
        {...{ dataTestId, icon, isDisabled, isExpanded, onClick }}
        {...other}
        ref={ref}
      />
    );
  }
);

Fab.propTypes = {
  /** @ignore */
  backgroundAppearance: PropTypes.oneOf(Object.values(BUTTON_BACKGROUND_APPEARANCES)),
  /** Id value used for testing */
  dataTestId: PropTypes.string,
  /** If not loading, will render specified icon before text */
  icon: PropTypes.node,
  /** If true, will allow button to collapse */
  isCollapsible: PropTypes.bool,
  /** Disallows user to interact with the button and adjusts appearance */
  isDisabled: PropTypes.bool,
  /** Callback that is called on click */
  onClick: PropTypes.func,
  /** Will display text inside button */
  text: PropTypes.node.isRequired,
};

export { Fab };
